import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import "../App.scss";
import "../components/components.scss";
import "./containers.scss";
import "./pageContents/pageContent.scss";
import "../components/staticComponents/popups.scss";
import TestPages from "./pageContents/TestPages";
import { parseAnswers } from "./pageContents/Const";
import { prepareTestPages } from "../utils/prepareTest";
import { buildSubmitAnswers } from "../utils/submitAnswers";

// ------------------------------------------------------------
// 預覽模式（/preview-{預覽碼}）：教務出完題，用考生一模一樣的作答畫面試做一次。
//
// 與正式作答的差別：不計時、不鎖定、不偵測離開、不自動存檔、不建立任何考試紀錄。
// 「對答案」把作答用交卷同一支 buildSubmitAnswers 轉好，送到伺服器用交卷同一套
// 批改規則判對錯（只回結果、不寫資料）——答案鍵設錯在這裡就看得出來。
//
// 預覽碼由模考後台（持 api_token）換來，兩小時有效；考試站本身不持任何金鑰。
// ------------------------------------------------------------

const TYPE_WORDING = { L: "Listening 聽力", R: "Reading 閱讀", W: "Writing 寫作" };

const Bar = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: #3e2f1f;
  color: #fff;
  font-size: 14px;
  text-align: left;

  .ce-pv-badge {
    background: #e2704e;
    border-radius: 4px;
    padding: 2px 8px;
    font-weight: 700;
  }
  .ce-pv-title {
    font-weight: 700;
  }
  .ce-pv-note {
    opacity: 0.7;
    font-size: 12px;
  }
  .ce-pv-pages {
    display: flex;
    gap: 4px;
    margin-left: auto;
  }
  .ce-pv-btn {
    border: 1px solid rgba(255, 255, 255, 0.4);
    background: transparent;
    color: #fff;
    border-radius: 4px;
    padding: 3px 10px;
    cursor: pointer;
    font-size: 13px;
  }
  .ce-pv-btn.is-active,
  .ce-pv-btn--primary {
    background: #e2704e;
    border-color: #e2704e;
  }
  audio {
    height: 32px;
  }
`;

const Result = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;
  text-align: left;

  .ce-pv-card {
    background: #fff;
    border-radius: 10px;
    width: min(760px, 100%);
    padding: 20px 24px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }
  th,
  td {
    border-bottom: 1px solid #eee;
    padding: 6px 8px;
    vertical-align: top;
  }
  .ok {
    color: #2e7d32;
    font-weight: 700;
  }
  .no {
    color: #c62828;
    font-weight: 700;
  }
`;

const PreviewApp = () => {
  // 網址只能有一層：index.html 用相對路徑載入 js/jquery 等九支外部檔，
  // 兩層的 /preview/{碼} 會變成 /preview/js/... 而載不到
  const code = decodeURIComponent(window.location.pathname.replace(/^\/preview-/, "").split("/")[0] || "");
  const [test, setTest] = useState(null);
  const [error, setError] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [storedAnswers, setStoredAnswers] = useState([]);
  const [writingAnswerNote, setWritingAnswerNote] = useState([]);
  const [answeredQuestion, setAnsweredQuestion] = useState([]);
  const [reviewModeNode, setReviewModeNode] = useState([]);
  const [splitRatio, setSplitRatio] = useState(50);
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    document.title = "預覽模式 - IELTS Mock Test";
    axios
      .get(`${process.env.REACT_APP_PREVIEW}/${encodeURIComponent(code)}`)
      .then(({ data }) => {
        if (!data.success) {
          setError(data.message || "預覽連結無效");
          return;
        }
        // 轉換會改寫 pages，先複製一份
        const pages = prepareTestPages(JSON.parse(JSON.stringify(data.test.pages)), data.test.type);
        setTest({ ...data.test, pages });
      })
      .catch((err) => {
        const msg = err.response && err.response.data && err.response.data.message;
        setError(msg || "預覽載入失敗，請回後台重新開啟預覽");
      });
  }, [code]);

  const answerKey = useMemo(() => (test ? parseAnswers(test.pages, test.answers) : []), [test]);

  const checkAnswers = () => {
    setChecking(true);
    const submit = buildSubmitAnswers(test.type, answerKey, storedAnswers, test.pages);
    const body = new URLSearchParams();
    body.append("stored_answers", JSON.stringify(submit));
    axios
      .post(`${process.env.REACT_APP_PREVIEW}/${encodeURIComponent(code)}/grade`, body)
      .then(({ data }) => setResult(data))
      .catch((err) => {
        const msg = err.response && err.response.data && err.response.data.message;
        setResult({ success: false, message: msg || "對答案失敗，請稍後再試" });
      })
      .finally(() => setChecking(false));
  };

  const resetAll = () => {
    setStoredAnswers([]);
    setWritingAnswerNote([]);
    setAnsweredQuestion([]);
    setReviewModeNode([]);
    setResult(null);
  };

  if (error) {
    return <div style={{ padding: "60px 20px", fontSize: "18px" }}>{error}</div>;
  }
  if (!test) {
    return <div style={{ padding: "60px 20px" }}>預覽載入中…</div>;
  }

  const pageNumber = 4 + pageIndex;
  const goPage = (n) => {
    const idx = n - 4;
    if (idx >= 0 && idx < test.pages.length) {
      setPageIndex(idx);
    }
  };

  return (
    <div className="App">
      <Bar>
        <span className="ce-pv-badge">預覽模式</span>
        <span className="ce-pv-title">{test.title}</span>
        <span>{TYPE_WORDING[test.type] || test.type}</span>
        <span className="ce-pv-note">不計時、不存檔、不會建立考試紀錄</span>
        {test.type === "L" && test.audio && (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio controls preload="metadata" src={test.audio} />
        )}
        <span className="ce-pv-pages">
          {test.pages.map((p, i) => (
            <button
              key={i}
              type="button"
              className={`ce-pv-btn ${i === pageIndex ? "is-active" : ""}`}
              onClick={() => setPageIndex(i)}
            >
              {test.type === "L" ? `Part ${i + 1}` : test.type === "W" ? `Task ${i + 1}` : `Passage ${i + 1}`}
            </button>
          ))}
          {test.type !== "W" && (
            <button type="button" className="ce-pv-btn ce-pv-btn--primary" onClick={checkAnswers} disabled={checking}>
              {checking ? "批改中…" : "對答案"}
            </button>
          )}
          <button type="button" className="ce-pv-btn" onClick={resetAll}>
            清除作答
          </button>
        </span>
      </Bar>

      <div className="main-content-container full-height">
        <TestPages
          testPages={test.pages}
          pageNumber={pageNumber}
          pageType="Tests"
          testType={test.type}
          candidate={null}
          renderNextPage={() => goPage(pageNumber + 1)}
          renderPreviousPage={() => goPage(pageNumber - 1)}
          TestsPageJump={goPage}
          storedAnswers={storedAnswers}
          setStoredAnswers={setStoredAnswers}
          writingAnswerNote={writingAnswerNote}
          setWritingAnswerNote={setWritingAnswerNote}
          answeredQuestion={answeredQuestion}
          setAnsweredQuestion={setAnsweredQuestion}
          reviewModeNode={reviewModeNode}
          setReviewModeNode={setReviewModeNode}
          splitRatio={splitRatio}
          setSplitRatio={setSplitRatio}
          fontScale={1}
        />
      </div>

      {result && (
        <Result onClick={(e) => e.target === e.currentTarget && setResult(null)}>
          <div className="ce-pv-card">
            {!result.success ? (
              <p>{result.message}</p>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>
                  對答案：{result.correct} / {result.total}（Band {result.band}）
                </h3>
                <p style={{ color: "#666", fontSize: "13px" }}>
                  用交卷時同一套規則批改。若「正解」欄看起來不對，代表答案設定可能有誤。
                </p>
                <table>
                  <thead>
                    <tr>
                      <th>題號</th>
                      <th />
                      <th>你的作答</th>
                      <th>正解</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.results || []).map((r) => (
                      <tr key={r.seq}>
                        <td>{r.seq}</td>
                        <td className={r.ok ? "ok" : "no"}>{r.ok ? "✔" : "✘"}</td>
                        <td>{r.answered ? r.student : "（未作答）"}</td>
                        <td>{(r.expected || []).join(" / ") || "（答案未設定）"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            <button type="button" className="btn btn-secondary mt-3" onClick={() => setResult(null)}>
              關閉，繼續作答
            </button>
          </div>
        </Result>
      )}
    </div>
  );
};

export default PreviewApp;
